import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { InspirationQualityStats } from '@/utils/inspirationQuality';

interface UseInspirationQualityResult {
  stats: InspirationQualityStats[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useInspirationQuality(userId: string | null): UseInspirationQualityResult {
  const [stats, setStats] = useState<InspirationQualityStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Query the view via raw SQL since it's not in generated types yet
      const { data, error: queryError } = await supabase
        .from('inspiration_quality_stats' as any)
        .select('*')
        .eq('user_id', userId);

      if (queryError) {
        console.error('Inspiration quality stats error:', queryError);
        setError('Nie udało się pobrać statystyk jakości');
        return;
      }

      setStats((data as unknown as InspirationQualityStats[]) || []);
    } catch (err) {
      console.error('Unexpected error fetching quality stats:', err);
      setError('Wystąpił nieoczekiwany błąd');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchStats();
    }
  }, [userId, fetchStats]);

  return { stats, isLoading, error, refresh: fetchStats };
}
